import { Card, Grid } from '@design-systems/canvas-ui'
import { useEssentials } from 'hooks/utils/useEssentials'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { useStoreTrailers } from 'hooks/trailer/useStoreTrailers'
import useCountdown from 'hooks/utils/useCountdown'
import { BasicPlaceholder } from 'components/placeholder'
import { ErrorAlert } from 'components/error-fallback'
import { Table } from 'components/table-compat/table-compat'
import { useMemo } from 'react'
import { transformShipmentsForTable } from './helpers'
import { IStoreTrailerDetails } from './store-trailer-details/ITrailer'

export const TrailerSummaryTab = () => {
    const { currentLocation, env, logout, session, t } = useEssentials()

    const { data, isLoading, isError, error, refetch } = useStoreTrailers({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
    })

    const count = useCountdown(5, logout, error as Error)

    const {
        trailer: { summary },
    } = TRANSLATION_KEYS

    const sections = useMemo(
        () => ({
            unacknowledged_at_store: t(summary.unacknowledgedStoreTitle),
            unacknowledged_in_transit: t(summary.unacknowledgedTransitTitle),
            unacknowledged_at_dc: t(summary.unacknowledgedDcTitle),
            unacknowledged_no_tracking: t(
                summary.unacknowledgedNoTrackingTitle,
            ),
            acknowledged: t(summary.acknowledgedTitle),
        }),
        [summary, t],
    )

    const transformedData = useMemo(() => {
        if (!data) return {}
        const newTransformed: Record<string, any> = {}
        Object.keys(sections).forEach((key) => {
            const shipmentData = data[key as keyof IStoreTrailerDetails]
            if (Array.isArray(shipmentData)) {
                newTransformed[key] = transformShipmentsForTable(
                    shipmentData,
                    currentLocation,
                )
            }
        })
        return newTransformed
    }, [data, sections, currentLocation])

    if (isLoading) {
        return (
            <div className="placeholder-container" data-testid="placeholder">
                <BasicPlaceholder type="text" rows={1} />
                <BasicPlaceholder type="table" rows={8} columns={3} />
            </div>
        )
    }

    if (isError || !data) {
        return (
            <ErrorAlert error={error} count={count} onRetry={() => refetch()} />
        )
    }

    return (
        <Grid.Container spacing="expanded">
            {Object.entries(sections).map(([key, title], index) =>
                key in data ? (
                    <Card className="hc8-pa-3x" key={`trailer-${key}`}>
                        <h3 className="hc8-fs-050">{title}</h3>

                        <Table
                            data={transformedData[key]}
                            alternateRowColor={true}
                        />
                    </Card>
                ) : null,
            )}
        </Grid.Container>
    )
}
